<template>
  <section class="course-cover grid">
    <div class="wrapper">
      <div class="content">
        <div class="heading">
          <p
            class="level t-p-s-130"
            v-html="nbsp(t(`course-level.${course.course_level}`))"
          />
          <h1 class="title t-h1" v-html="nbsp(course.title)" />
          <p class="program t-p-m-130" v-html="nbsp(course.name)" />
        </div>
        <div class="button-row">
          <p class="price-range t-h5">
            {{ price }}
          </p>

          <BaseButton
            v-if="course.timetable_url"
            color="outline-black"
            size="l"
            :base-link="{
              ...course.timetable_url,
              label: t('course-cover.check-dates')
            }"
          />
          <BaseButton
            v-else
            :tag="NuxtLink"
            :to="
              useLocalePath()({
                name: 'slug',
                params: { slug: t('timetable.slug') },
                query: { course: props.course.program_type_id }
              })
            "
            color="outline-black"
            :text="t('course-cover.check-dates')"
            size="l"
          />
        </div>
      </div>
      <div class="image-wrapper">
        <BaseImage class="fill-space" :image="course.image" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'
import type { PublicCourseAndEventPage } from '@/service/__generated-api'

const { t } = useI18n()
const props = defineProps<{ course: PublicCourseAndEventPage }>()

const price = computed(() => {
  let priceRange = `${props.course.price_range?.min} - ${props.course.price_range?.max} ${props.course.price_range?.currency}`

  if (!props.course.price_range) priceRange = t('no-data')

  return t('course-cover.price', {
    priceRange
  })
})
</script>

<style scoped lang="scss">
.course-cover {
  margin-bottom: rem(90px);
}

.wrapper {
  @include column(xs, 12);
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;
  border-radius: $border-radius;

  @include media-up(s-lg) {
    position: relative;
    z-index: 0;
    min-height: rem(459px + (14px * 2));
    padding: rem(14px);
    margin-inline: rem(-20px);
    border-radius: $border-radius-mid;
  }

  @include media-up(s-xl) {
    margin-inline: rem(-100px);
  }
}

.content {
  padding: rem(24px) rem(20px) rem(32px);
  background-color: $layout-background-02;

  @include media-up(md) {
    padding-inline: rem(40px);
  }

  @include media-up(s-lg) {
    @include flex-col;
    flex-grow: 1;
    justify-content: space-between;
    width: 50%;
    padding: rem(60px) rem(40px) rem(60px) rem(46px);
    border-radius: $border-radius;
  }

  @include media-up(lg) {
    padding-inline: rem(86px) rem(80px);
  }
}

.level {
  margin-bottom: rem(24px);
  color: $layout-text-03;
}

.title {
  margin-bottom: rem(16px);
}

.program {
  margin-bottom: rem(20px);
}

.button-row {
  @include flex-col;
  gap: rem(24px);

  @include media-up(s-lg) {
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
  }
}

.image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2;

  @include media-up(s-lg) {
    position: absolute;
    inset: 0;
    z-index: -1;
    aspect-ratio: unset;
  }
}
</style>
